import Disaster from './Disaster';
import AlertPopup from '../ui/AlertPopup';
import common from '../common';
import ParticleEmitter from '../ParticleEmitter';
import { AdjustmentFilter } from 'pixi-filters';

export default class Flood extends Disaster {
	constructor(world) {
		super(world);

		this.floodLevel = 12;
		this.floodRate = 2;
		this.floodCount = 19;

		this._canUpdate = true;

		this._rainTextures = [
			'droplet_01',
			'droplet_02',
			'droplet_03',
			'droplet_04',
			'droplet_05',
			'droplet_06',
			'droplet_07',
		];

		this._rainEmitter = null;

		this.minX = (P3.View.width - common.STAGE_WIDTH) * 0.5;
		this.maxX = (common.STAGE_WIDTH + P3.View.width) * 0.5;
		this.minY = 0;
		this.maxY = common.STAGE_HEIGHT;

		this._sourceTile = this._world.getTile(3, 29);
		this._floodTiles = [];

		this._floodTween = null;

		this._destroyed = false;

		for (let y = 0, rows = this._world.rows; y < rows; ++y) {
			for (let x = 0, columns = this._world.columns; x < columns; ++x) {
				const tile = this._world.getTile(x, y);

				this.initTile(tile);
			}
		}

		this._adfilter = new AdjustmentFilter();
		common.world.filterArea = new PIXI.Rectangle(0, 0, P3.View.width, P3.View.height);
		common.world.filters = [this._adfilter];

		this._ambientSound = P3.audio.playSound('sfx_fd_ambience_loop_00', { loop: true });

		this._maxDroplets = 300;
		this._lastDropletSpawned = Date.now();

		this._darkening = false;
		this._rainCooldown = { c: 1000 };

		this._droplets = new PIXI.ParticleContainer();
		this._world.addChild(this._droplets);
	}

	destroy() {
		if (this._rainEmitter) {
			this._rainEmitter.stop();
			this._rainEmitter.visible = false;
			this._rainEmitter.destroy({ children: true });
		}

		this._droplets.destroy({ children: true });
		this._canUpdate = false;

		this._destroyed = true;

		super.destroy();
	}

	_createDroplet() {
		let drop = new PIXI.Sprite(P3.assets.texture(P3.utils.selectRandom(this._rainTextures)));

		drop.anchor.set(0.5);
		drop.speed = P3.math.randomInt(5, 60);
		drop.position.set(P3.math.randomInt(this.minX, this.maxX), P3.math.randomInt(this.minY, this.maxY));

		const fadeTween = new TWEEN.Tween(drop, this._tweens)
			.to({
				alpha: 0,
			}, 1000)
			.easing(TWEEN.Easing.Linear.None)
			.onComplete(() => {
				if (drop.parent) {
					drop.destroy();
					drop = null;
				}
			});

		new TWEEN.Tween({}, this._tweens)			// FIXME: what is this? is this just a delay? why not add .delay(5000) to fadeTween?
			.to({}, 5000)
			.easing(TWEEN.Easing.Linear.None)
			.onComplete(() => {
				fadeTween.start();
			}, this)
			.start();

		return drop;
	}

	update() {
		if (!this._canUpdate) {
			return;
		}

		super.update();

		if (this._droplets) {
			for (const drop of this._droplets.children) {
				drop.y += drop.speed * window.dt;
			}
		}

		if (this._rainEmitter) {
			this._rainEmitter.update();
		}

		if (this._canRain) {
			this.rain();
		}

		for (let i = 0; i < this._floodTiles.length; ++i) {
			const tile = this._floodTiles[i];

			!tile.data.structure && (tile.level += (tile.flood.level - tile.level) * 0.02);
		}
	}

	initTile(tile) {
		tile.flood = {
			level: tile.level,
			flooded: false,
		};
		tile.ground === 1 && this._floodTiles.push(tile);
	}

	start() {
		if (super.start()) {
			this.darken(4000, true);
			P3.audio.playSound('sfx_fd_disaster_strikes_00', {
				callback: () => {
					this.end();
				},
			});
			let count = this.floodCount;

			const surge = () => {
				if (count-- > 0) {
					P3.utils.delayg(this._tweens, 1, surge, null, this);
					this.startFlood(this._sourceTile);
				}
			};

			surge.call(this);

			const t = { value: 1 };

			this._floodTween && this._floodTween.stop();
			this._floodTween = new TWEEN.Tween(t, this._tweens)
				.to({ value: 2 }, 600)
				.easing(TWEEN.Easing.Cubic.InOut)
				.onUpdate(() => {
					this._world.waveFrequency = t.value;
				})
				.start();
		}
	}

	rain() {
		if (this._droplets.children.length >= this._maxDroplets) {
			return;
		}
		if (Date.now() - this._lastDropletSpawned > this._rainCooldown.c) {
			this._lastDropletSpawned = Date.now();
			this._droplets.addChild(this._createDroplet());
		}
	}

	darken(t = 20000, override = false) {
		if (this._darkening && !override) {
			return;
		}
		this._darkening = true;
		this._canRain = true;

		if (this._rainEmitter) {
			this._rainEmitter.destroy({ children: true });
		}

		this._rainEmitter = new ParticleEmitter('particle_rain_angle_emitter', [
			P3.assets.texture('particle_raindrop_00'),
		], true);
		this._rainEmitter.alpha = 0;
		this._rainEmitter.enableLoop();
		this._rainEmitter.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
		this._world.addChild(this._rainEmitter);
		this._rainEmitter.play();
		this.resize();

		new TWEEN.Tween(this._rainEmitter, this._tweens)
			.to({ alpha: 1 }, 20000)
			.easing(TWEEN.Easing.Linear.None)
			.start();

		new TWEEN.Tween(this._rainCooldown, this._tweens)
			.to({ c: 50 }, t * 0.5)
			.easing(TWEEN.Easing.Linear.None)
			.start();
		new TWEEN.Tween(this._adfilter, this._tweens)
			.to({
				saturation: 0.4,
				contrast: 1.3,
				brightness: 0.7,
			}, t)
			.easing(TWEEN.Easing.Linear.None)
			.start();
	}

	startFlood(tile) {
		const flood = {
			tiles: [tile],
			visited: [tile],
			spread: 2000, // 28
			level: NaN,
		};

		this.floodTile(tile);
		this.stepFlood(flood);
	}

	stepFlood(flood) {
		flood.tween && flood.tween.stop();
		flood.tween = P3.utils.delayg(this._tweens, 0.04, () => {
			const tiles = flood.tiles.slice();

			flood.tiles.length = 0;

			let floodCount = 0;

			for (let i = 0; i < tiles.length; ++i) {
				const tile = tiles[i];

				!tile.data.structure && (tile.ground = P3.utils.selectRandom([229, 230]));

				let spreadCount = 0;
				const neighbours = tile.neighbours;

				for (let j = 0; j < neighbours.length; ++j) {
					const neighbour = neighbours[j];

					if (!(neighbour && flood.visited.indexOf(neighbour) === -1)) {
						continue;
					}

					const level = neighbour.flood.level + Math.ceil(neighbour.strength / 3);

					if (level > tile.flood.level) {
						continue;
					}

					flood.visited.push(neighbour);

					if (neighbour.data.structure && neighbour.data.structure.type === 0) {
						const strength = neighbour.data.structure.strength * 3;

						if (Math.random() < strength / 100) {
							continue;
						}
					}

					if (!neighbour.isWater && flood.spread-- <= 0) {
						continue;
					}

					flood.tiles.indexOf(neighbour) === -1 && flood.tiles.push(neighbour);
					this._floodTiles.indexOf(neighbour) === -1 && this._floodTiles.push(neighbour);

					this.floodTile(neighbour);

					if (isNaN(flood.level) && neighbour.flood.level <= this.floodLevel) {
						neighbour.flood.level < this.floodLevel && (neighbour.flood.level += this.floodRate);
						flood.level = neighbour.flood.level;
					} else if (neighbour.flood.level <= flood.level) {
						neighbour.flood.level = flood.level;
					}
					++spreadCount;
				}
				spreadCount > 0 && ++floodCount;
			}
			floodCount > 0 && this.stepFlood(flood);
		}, null, this);
	}

	floodTile(tile) {
		if (tile.data.structure) {
			this.damageStructure(tile);
		} else {
			tile.ground = P3.utils.selectRandom([226, 228]);
		}
	}

	damageStructure(tile) {
		if (tile.damaged) {
			return;
		}
		tile.damaged = true;
		tile.ground = 225;

		const structure = tile.data.structure;

		if (structure && structure.population > 0) {
			let type;
			let dead;
			let injured;
			let survive;
			let cost;

			if (Math.random() < 0.25) {
				type = AlertPopup.TYPE_DEAD;
				dead = structure.population * 0.5;
				cost = structure.cost * 0.8;
				dead = Math.random() > 0.5 ? Math.ceil(dead) : Math.floor(dead);
				injured = Math.max(0, structure.population - dead);
				survive = 0;

				this._hospitalDestroyed = structure.special && structure.special === 'hospital';
				this._schoolDestroyed = structure.special && structure.special === 'school';

				this._totalDestroyed++;
			} else if (Math.random() < 0.5) {
				type = AlertPopup.TYPE_CRITICAL;
				dead = structure.population * 0.6;
				dead = Math.random() > 0.5 ? Math.ceil(dead) : Math.floor(dead);
				cost = structure.cost * 0.25;
				injured = structure.population - dead;
				survive = 0;
				this._totalDestroyed++;
			} else {
				type = AlertPopup.TYPE_INJURED;
				dead = 0;
				cost = structure.cost * 0.3;
				injured = structure.population;
				survive = 0;
			}
			tile.parent && tile.alert.start(type);

			this._totalDeaths += dead;
			this._totalInjury += injured;
			this._totalSurvive += survive;
			this._totalCost += cost;
		}
	}

	resize() {
		common.world.filterArea = new PIXI.Rectangle(0, 0, P3.View.width, P3.View.height);

		this.minX = (P3.View.width - common.STAGE_WIDTH) * 0.5;
		this.maxX = (common.STAGE_WIDTH + P3.View.width) * 0.5;
		this.minY = 0;
		this.maxY = common.STAGE_HEIGHT;

		if (this._rainEmitter) {
			this._rainEmitter.width = P3.View.width;
			this._rainEmitter.height = P3.View.height;
		}
	}

	end() {
		this._canRain = false;

		new TWEEN.Tween(this._rainEmitter, this._tweens)
			.to({ alpha: 0 }, 4000)
			.easing(TWEEN.Easing.Linear.None)
			.onComplete(() => {
				this._rainEmitter.stop();
				this._rainEmitter.destroy({ children: true });
			})
			.start();

		new TWEEN.Tween(this._adfilter, this._tweens)
			.to({
				saturation: 1,
				contrast: 1,
				brightness: 1,
			}, 4000)
			.easing(TWEEN.Easing.Linear.None)
			.onComplete(() => {
				common.world.filters = null;
			});
		// .start();

		this._droplets.destroy({ children: true });

		const unhoused = common.scenario.population.unhoused;

		this._totalDeaths += P3.math.randomInt(0, unhoused); // kills a random amount of unhoused civs
		this._totalDeaths *= Math.max(1 - (common.scenario._ccUpgrades.length / 10), 0.5);
		this._totalDeaths = Math.floor(this._totalDeaths);
		this._totalSurvive = common.scenario.population.total - this._totalDeaths;

		if (!this._destroyed) {
			this.signals.disasterEnd.dispatch(this);
		}

		this._isRunning = false;
		this._canStart = false;
	}
}
